import React from "react";
import { feature } from "topojson-client";
import countryData from "../../data/countries.json";
import models from "../../data/models.json";
import Vizualisation from "../../../../components/Vizualisation";
import ColorLegend from "../../../../components/ColorLegend/colorLegend";
import useVizualisation from "../../../../helpers/useVizualisation";
import { geoEqualEarth, geoPath } from "d3-geo";
import { scaleOrdinal } from "d3-scale";

const margin = {
  top: 10,
  right: 10,
  bottom: 10,
  left: 10,
};

const ChoroplethMap = ({
  data,
  title,
  note,
  source,
  value,
  highlight,
  open,
  conditional,
  limited,
  outside,
}) => {
  const { ref, width, height, windowSize, inView } = useVizualisation();

  const mapHeight =
    width < 700 ? windowSize.height - 100 : windowSize.height - 75;

  const countries = feature(countryData, countryData.objects.countries);
  const disputed = feature(countryData, countryData.objects.disputed);
  const borders = feature(countryData, countryData.objects.boundaries);
  const coastline = feature(countryData, countryData.objects.coastline);

  const highlightMap = {
    none: [],
    open: models
      .filter((d) => d.frame == "Open transfer")
      .map((d) => d.country),
    openLacking: ["PAK", "SAU", "BOL", "KHM"],
    usaBgd: ["USA", "BGD"],
    conditional: models
      .filter((d) => d.frame == "Conditional transfer")
      .map((d) => d.country),
    gdprLike: ["ARG", "COL", "EST", "KOR", "MAL", "SEN", "SAF"],
    government: models
      .filter((d) => d.frame == "Limited transfer")
      .map((d) => d.country),
    china: ["CHN"],
    governmentExamples: ["VNM", "RUS"],
    all: countries.features.map((d) => d.properties.id),
  };

  const projection = geoEqualEarth()
    .fitSize([width - 10, mapHeight - 10], countries)
    .rotate([-10, 0]);
  const path = geoPath().projection(projection);

  const linetype = new Map([
    ["solid", ""],
    ["11", "0 1.5"], // round endcaps give a zero-length line lenght 0.5 + 0.5 so this creates dots
    ["31", "3 1"],
  ]);

  function getModel(cntr) {
    if (models.filter((d) => d.country === cntr).length > 0) {
      return models.filter((d) => d.country === cntr)[0].frame;
    } else {
      return "Outside sample";
    }
  }

  const translateMap = {
    "Open transfer": open,
    "Conditional transfer": conditional,
    "Limited transfer": limited,
    "Outside sample": outside,
  };

  const colorScale = scaleOrdinal()
    .domain([
      "Open transfer",
      "Conditional transfer",
      "Limited transfer",
      "Outside sample",
    ])
    .range(["#31989E", "#F2C118", "#481F7C", "#AAAAAA"]);

  const colorScaleTranslated = scaleOrdinal()
    .domain(
      [
        "Open transfer",
        "Conditional transfer",
        "Limited transfer",
        "Outside sample",
      ].map((d) => translateMap[d])
    )
    .range(["#31989E", "#F2C118", "#481F7C", "#AAAAAA"]);

  return (
    <>
      <ColorLegend colorscale={colorScaleTranslated}></ColorLegend>
      <Vizualisation
        title={title}
        note={note}
        source={source}
        ref={ref}
        size="xLarge"
      >
        <svg width={width} height={mapHeight}>
          <g
            className="chart"
            transform={`translate(${margin.left}, ${margin.top})`}
          >
            <g className="country-layer">
              {countries.features.map((d, i) => {
                return (
                  <path
                    key={`key-${i}`}
                    className="country"
                    d={path(d)}
                    fill={colorScale(getModel(d.properties.id))}
                    opacity={
                      highlightMap[highlight].includes(d.properties.id)
                        ? 1
                        : 0.1
                    }
                  ></path>
                );
              })}
            </g>
            <g className="disputed-layer">
              {disputed.features.map((d, i) => {
                return (
                  <path
                    key={`key-${i}`}
                    className="disputed"
                    d={path(d)}
                    fill="#f1f1f1"
                    opacity={0.3}
                  ></path>
                );
              })}
            </g>
            <g className="border-layer">
              {borders.features.map((d, i) => {
                return (
                  <path
                    key={`key-${i}`}
                    className="borders"
                    d={path(d)}
                    fill="none"
                    stroke="#ffffff"
                    strokeWidth={0.5}
                    strokeDasharray={linetype.get(d.properties.linetype)}
                    strokeLinecap={d.properties.lineend}
                  ></path>
                );
              })}
            </g>
            <g className="coastline-layer">
              {coastline.features.map((d, i) => {
                return (
                  <path
                    key={`key-${i}`}
                    className="coastline"
                    d={path(d)}
                    fill="none"
                    stroke="#333333"
                    strokeWidth={0.2}
                  ></path>
                );
              })}
            </g>
          </g>
        </svg>
      </Vizualisation>
    </>
  );
};

export default ChoroplethMap;
